import {
    Avatar,
    Box,
    Button,
    Divider,
    Flex,
    List,
    ListItem,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    Spacer,
    Spinner,
    Stack,
    Text,
    Tooltip
} from "@chakra-ui/react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
    MdAdd,
    MdCamera,
    MdCameraRoll,
    MdRingVolume,
    MdVideocam,
    MdVideocamOff,
    MdVolumeMute,
    MdVolumeUp
} from "react-icons/md";
import { Prompt } from "react-router-dom";
import Participants from "./participants";
import JitsiMeet from "../../components/JitsiMeet";
import TwilioRoom from "../../components/Twilio/Room";
import {
    AccountRole,
    IncomingCallSubscription,
    MyCallsDocument,
    ParticipantRole,
    useAccountsQuery,
    useInviteMutation,
    useTerminateCallMutation
} from "../../graphql";
import { useAuthToken } from "../../hooks";
import { ShortAccount, getFullName } from "../../utils";
import { PatientBox } from "../Patient/box";

type IVideoConferenceProps = {
  data: IncomingCallSubscription;
};

export const VideoConference: React.FC<IVideoConferenceProps> = ({ data }) => {
    const { t } = useTranslation();
    const [mute, setMute] = useState(false);
    const [disableVideo, setDisableVideo] = useState(true);

    const { accountId } = useAuthToken();
    const {
        data: accountsData,
        loading: accountsLoading,
        error
    } = useAccountsQuery({
        variables: {
            role: AccountRole.Doctor,
            page: 1,
            pageLimit: 1000
        }
    });

    const call = data?.incomingCall?.call;

    const [
        terminateCall,
        { loading: terminatingInFLight }
    ] = useTerminateCallMutation({
        refetchQueries: [{ query: MyCallsDocument }]
    });

    const [inviteGuest, { loading: invitationLoading }] = useInviteMutation();

    const onLeave = () => {
        terminateCall({
            variables: { id: call?.id }
        });
    };

    const onInvite = (callId, accountId) => {
        inviteGuest({
            variables: { callId, accountId }
        });
    };

    const usersInCall = call.participants.map((p) => p.account.id);
    const doctorsNotInCall = !accountsLoading
        ? accountsData.accounts.nodes.filter((a) => !usersInCall.includes(a.id))
        : [];

    const me = call?.participants?.find((p) => p.account.id === accountId);
    const caller = call?.participants?.find(
    (p) => p.role === ParticipantRole.Caller
  );

    return (
        <>
            <Prompt
                when={ !!call }
                message={ (location) =>
                `Nëse largoheni nga kjo faqe biseda do të nderpritet!`
                }
            />
            <Box bg="white" p={ 2 }>
                <Flex>
                    <Box>
                        <Participants participants={ call.participants } />
                    </Box>
                    <Spacer />
                    <Stack direction="row" spacing={ 4 }>
                        <Divider orientation="vertical" />

                        <Button
                            colorScheme="red"
                            variant="solid"
                            size="sm"
                            onClick={ onLeave }
                        >
                            { t("calls.terminateCall") }
                        </Button>

                        <Divider orientation="vertical" />

                        <Tooltip label={ mute ? t("calls.onMic") : t("calls.offMic") }>
                            <Button
                                colorScheme="grey"
                                variant="outline"
                                size="sm"
                                onClick={ () => setMute(!mute) }
                            >
                                { mute ? <MdVolumeUp /> : <MdVolumeMute /> }
                            </Button>
                        </Tooltip>

                        <Tooltip
                            label={ disableVideo ? t("calls.onCam") : t("calls.offCam") }
                        >
                            <Button
                                colorScheme="grey"
                                variant="outline"
                                size="sm"
                                onClick={ () => setDisableVideo(!disableVideo) }
                            >
                                { disableVideo ? <MdVideocam /> : <MdVideocamOff /> }
                            </Button>
                        </Tooltip>

                        <Divider orientation="vertical" />

                        <Popover>
                            <PopoverTrigger>
                                <Button
                                    rightIcon={ <MdAdd /> }
                                    colorScheme="blue"
                                    variant="outline"
                                    size="sm"
                                >
                                    { t("calls.invitieGuest") }
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent>
                                <PopoverArrow />
                                <PopoverCloseButton />
                                <PopoverHeader>{ t("calls.listOfDoctors") }</PopoverHeader>
                                <PopoverBody>
                                    <List spacing={ 3 }>
                                        { !accountsLoading ? (
                                            doctorsNotInCall.length ? (
                                                doctorsNotInCall.map((account) => {
                                                    const fullName = getFullName(account as ShortAccount);

                                                    return (
                                                        <ListItem>
                                                            <Flex>
                                                                <Box w="40px">
                                                                    <Avatar
                                                                        size="sm"
                                                                        name={ fullName }
                                                                        src={ fullName }
                                                                    />
                                                                </Box>
                                                                <Box>
                                                                    <Text>{ fullName }</Text>
                                                                </Box>
                                                                <Spacer />
                                                                <Box>
                                                                    <Button
                                                                        isLoading={ invitationLoading }
                                                                        colorScheme="teal"
                                                                        size="xs"
                                                                        onClick={ () =>
                                                                            onInvite(call.id, account.id)
                                                                        }
                                                                    >
                                                                        { t("calls.invite") }
                                                                    </Button>
                                                                </Box>
                                                            </Flex>
                                                        </ListItem>
                                                    );
                                                })
                                            ) : (
                                                <Text>{ t("calls.noDocotrs") }</Text>
                                            )
                                        ) : (
                                            <Spinner />
                                        ) }
                                    </List>
                                </PopoverBody>
                            </PopoverContent>
                        </Popover>
                    </Stack>
                </Flex>
            </Box>
            { /* <JitsiMeet
        roomName={call.room}
        displayName={getFullName(me?.account as ShortAccount)}
      /> */ }

            <TwilioRoom
                handleLogout={ () => null }
                roomName={ call.room }
                // @ts-ignore
                token={ me.twilioToken }
                muteAudio={ mute }
                disableVideo={ disableVideo }
            />
            <PatientBox clientId={ caller.account.id } silent={ true } />
        </>
    );
};
